import { FilterListOutlined } from "@mui/icons-material";
import { Badge, Box, Button, ClickAwayListener, Popper } from "@mui/material";
import {
  PimoTableEventNames,
  PimoTableEventPayload,
} from "@pimo/pimo-components";
import SearchBarV2 from "@pimo/pimo-components/src/lib/pimo-components/search-bar-v2/search-bar-v2";
import {
  BusinessUnitType,
  Category,
  ExtendedLineOfBusiness,
  INITIATIVE_PHASES,
  InitiativeFilterData,
  InitiativeFilterOptions,
  InitiativePropertyFilters,
  LineOfBusiness,
  Phase,
  UpdateStatus,
} from "aint-types";
import { useState } from "react";

import { theme } from "../../app/theme";
import { InitiativesStatusTableFilterDialog } from "./initiatives-status-table-filter-dialog";

type InitiativesStatusTableFilterProps = {
  filterData: InitiativeFilterData;
  fireEvent?: (
    eventName: PimoTableEventNames,
    payload: PimoTableEventPayload
  ) => void;
  lineOfBusinessFilterOptions: (LineOfBusiness | ExtendedLineOfBusiness)[];
  showBusinessUnitFilter: boolean;
  showCategoryFilter: boolean;
};

export const InitiativesStatusTableFilter = ({
  filterData,
  fireEvent,
  lineOfBusinessFilterOptions,
  showBusinessUnitFilter,
  showCategoryFilter,
}: InitiativesStatusTableFilterProps) => {
  function isChangeEvent(
    event: React.ChangeEvent<HTMLInputElement> | { target: { value: string } }
  ): event is React.ChangeEvent<HTMLInputElement> {
    return !!(event.target as HTMLInputElement).name;
  }
  const clickAwayHandler = () => {
    setOpen(false);
  };
  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement> | { target: { value: string } },
    trigger: keyof InitiativePropertyFilters
  ) => {
    if (Array.isArray(filterData[trigger]) && isChangeEvent(event)) {
      const filterArray: string[] = filterData[trigger] ?? [];
      if (event.target.checked) {
        filterArray.push(event.target.name);
      } else {
        const doFilterData = () =>
          filterArray.filter(
            (uncheckedValue) => uncheckedValue !== event.target.name
          );

        switch (trigger) {
          case "updateStatusFilter":
            filterData[trigger] = doFilterData() as UpdateStatus[];
            break;

          case "phaseFilter":
            filterData[trigger] = doFilterData() as Phase[];
            break;

          case "lineOfBusinessFilter":
            filterData[trigger] = doFilterData() as LineOfBusiness[];
            break;
          case "businessUnitFilter":
            filterData[trigger] = doFilterData() as BusinessUnitType[];
            break;
          case "categoryFilter":
            filterData[trigger] = doFilterData() as Category[];
            break;
        }
      }
    } else {
      filterData.yearFilter = event.target.value;
    }

    fireEvent?.("filter", {
      filter: {
        ...filterData,
      },
    });
  };
  const handleSearchChange = (search: string) => {
    fireEvent?.("filter", {
      filter: {
        ...filterData,
        searchQueryFilter: search,
      },
    });
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);

  const clearAllFilters = () => {
    fireEvent?.("filter", {
      filter: {
        searchQueryFilter: "",
        yearFilter: "",
        phaseFilter: [],
        updateStatusFilter: [],
      },
    });
  };

  const filterOptions = {
    phase: [...INITIATIVE_PHASES],
    updateStatus: ["completed", "overdue", "pending"],
    lineOfBusiness: lineOfBusinessFilterOptions as (
      | LineOfBusiness
      | ExtendedLineOfBusiness
    )[],
  } as InitiativeFilterOptions;

  return (
    <Box
      sx={{
        display: "flex",
        color: "#122B54",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 2,
        marginLeft: "12px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: "1px solid",
          borderRadius: "4px",
          height: "37px",
          borderColor: "#b0b0b0",
          backgroundColor: "#ffffff",
          "&:hover": {
            backgroundColor: "#FCFCFC",
          },
        }}
      >
        <SearchBarV2 onChange={handleSearchChange} />
        <Box
          id={"STATUS_BUTTON_ID"}
          data-testid={"STATUS_BUTTON_ID"}
          sx={{
            backgroundColor: "#ffffff",
            marginRight: 1.5,
            cursor: "pointer",
            verticalAlign: "middle",
            borderRadius: "8px",
            p: 0.5,
            height: "30px",
            width: "30px",
          }}
          onClick={(e) => {
            setOpen(true);
            setAnchorEl(e.currentTarget);
          }}
        >
          <Badge
            variant="dot"
            invisible={!hasNonEmptyField(filterData)}
            sx={{
              "& .MuiBadge-dot": {
                backgroundColor: "primary.main",
              },
            }}
          >
            <FilterListOutlined />
          </Badge>
        </Box>
      </Box>
      {open && (
        <ClickAwayListener
          onClickAway={clickAwayHandler}
          mouseEvent={"onMouseUp"}
        >
          <Popper
            open={open}
            anchorEl={anchorEl}
            sx={{
              backgroundColor: "background.paper",
              borderColor: theme.palette.primary.main,
              boxShadow: "0px 0px 5px grey",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: 1,
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            <Box>
              <InitiativesStatusTableFilterDialog
                onClick={() => {
                  setOpen(false);
                }}
                handleChange={(event, trigger) => {
                  handleFilterChange(
                    event,
                    trigger as keyof InitiativePropertyFilters
                  );
                }}
                filterData={filterData}
                filterOptions={filterOptions}
                showBusinessUnitFilter={showBusinessUnitFilter}
                showCategoryFilter={showCategoryFilter}
              />
              <Button
                onClick={clearAllFilters}
                sx={{
                  border: "1px solid",
                  padding: "4px 16px",
                  borderCollapse: theme.palette.primary.dark,
                  borderRadius: "4px",
                  color: theme.palette.primary.main,
                  width: "300px",
                  height: "56px",
                }}
              >
                Clear All
              </Button>
            </Box>
          </Popper>
        </ClickAwayListener>
      )}
    </Box>
  );
};
function hasNonEmptyField(filters: InitiativeFilterData): boolean {
  return Object.values(filters).some(
    (field) => Array.isArray(field) && field.length > 0
  );
}
